<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Servicios a Domicilio</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Servicios Externos
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ servicios.length }}
          </span>
        </h1>
      </div>
      
      <button 
        @click="abrirModalCreacion"
        class="px-3 sm:px-6 py-2 sm:py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-[10px] sm:text-sm tracking-widest transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-2 w-full sm:w-auto justify-center"
      >
        <Icon icon="mdi:plus-circle" class="text-base sm:text-xl" />
        <span class="hidden sm:inline">NUEVO SERVICIO</span>
        <span class="sm:hidden">Nuevo</span>
      </button>
    </div>

    <!-- Filters & Search -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-7 lg:col-span-8">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Búsqueda Inteligente</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input
              v-model="filtroBusqueda"
              type="text"
              placeholder="Buscar por cliente, descripción, dirección o técnico..."
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
            />
          </div>
        </div>
        
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select
            v-model="filtroEstado"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="">TODOS</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="EN CAMINO">EN CAMINO</option>
            <option value="EN PROCESO">EN PROCESO</option>
            <option value="COMPLETADO">COMPLETADO</option>
            <option value="CANCELADO">CANCELADO</option>
          </select>
        </div>

        <div class="sm:col-span-2 lg:col-span-2">
          <button 
            @click="cargarServicios"
            class="w-full py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm"
          >
            <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" class="text-lg sm:text-xl" />
            <span class="hidden sm:inline">Refrescar</span>
            <span class="sm:hidden">Refrescar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
      <!-- Versión Desktop: Tabla completa -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Servicio</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Cliente</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Descripción</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Prioridad</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Fecha</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Total</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Técnico</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="9" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="!loading && serviciosFiltrados.length === 0">
              <td colspan="9" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:database-off-outline" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay servicios registrados</h4>
                </div>
              </td>
            </tr>
            <tr v-for="item in paginatedServicios" :key="item.idServicioExterno" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all group">
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800 transition-transform inline-block">
                  #{{ item.numeroServicio }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs">
                    {{ (item.nombreCliente || '?').charAt(0) }}
                  </div>
                  <p class="font-black text-gray-900 dark:text-white text-xs">{{ item.nombreCliente || 'N/A' }}</p>
                </div>
              </td>
              <td class="px-5 py-4">
                <p class="font-black text-gray-900 dark:text-white text-xs truncate max-w-[150px]" :title="item.descripcion">{{ item.descripcion || '-' }}</p>
              </td>
              <td class="px-5 py-4 text-center">
                <div :class="getPrioridadStyle(item.prioridad)" class="px-2.5 py-0.5 rounded-full text-[8px] font-black inline-block uppercase">
                  {{ item.prioridad || 'MEDIA' }}
                </div>
              </td>
              <td class="px-5 py-4 whitespace-nowrap">
                <div class="space-y-0.5">
                  <p class="font-black text-gray-900 dark:text-white text-xs">{{ formatearFecha(item.fechaProgramada) }}</p>
                  <p class="text-[9px] text-gray-400 font-black uppercase">{{ formatearHora(item.fechaProgramada) }}</p>
                </div>
              </td>
              <td class="px-5 py-4">
                <span class="font-black text-emerald-600 dark:text-emerald-400 text-sm">${{ (item.costo || 0).toFixed(2) }}</span>
              </td>
              <td class="px-5 py-4 text-center">
                <div :class="getEstadoStyle(item.estado)" class="px-3 py-1 rounded-lg text-[10px] font-black uppercase border shadow-sm inline-block">
                  {{ item.estado || 'PENDIENTE' }}
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-2">
                  <Icon icon="mdi:account-wrench" class="text-xs text-purple-500" />
                  <span class="text-[10px] font-black text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnico || 'Sin asignar' }}</span>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-center gap-0.5 flex-nowrap whitespace-nowrap">
                  <button @click="abrirModalEdicion(item)" class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Editar">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  <button v-if="item.estado !== 'COMPLETADO' && item.estado !== 'CANCELADO'" @click="abrirModalPago(item)" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Finalizar">
                    <Icon icon="mdi:check-circle" class="text-base" />
                  </button>
                  <button @click="confirmarEliminar(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-shrink-0" title="Eliminar">
                    <Icon icon="mdi:trash-can-outline" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-2">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!loading && serviciosFiltrados.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:database-off-outline" class="text-6xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay servicios registrados</h4>
          </div>
        </div>
        
        <div v-for="item in paginatedServicios" :key="item.idServicioExterno" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800">
                #{{ item.numeroServicio }}
              </span>
              <div :class="getEstadoStyle(item.estado)" class="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase border shadow-sm">
                {{ item.estado || 'PENDIENTE' }}
              </div>
            </div>
            <div :class="getPrioridadStyle(item.prioridad)" class="px-2 py-0.5 rounded-full text-[7px] font-black uppercase">
              {{ item.prioridad || 'MEDIA' }}
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2">
            <div class="w-7 h-7 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs flex-shrink-0">
              {{ (item.nombreCliente || '?').charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-black text-gray-900 dark:text-white text-xs leading-tight truncate">{{ item.nombreCliente || 'N/A' }}</p>
              <p class="text-[8px] font-black text-gray-400 truncate">{{ item.descripcion || '-' }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-1 mb-2 text-[10px]">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:calendar" class="text-gray-400 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400">{{ formatearFecha(item.fechaProgramada) }}</span>
            </div>
            <div class="flex items-center gap-1 justify-end">
              <span class="font-black text-emerald-600 dark:text-emerald-400">${{ (item.costo || 0).toFixed(2) }}</span>
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2 text-[9px] bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <Icon icon="mdi:account-wrench" class="text-purple-500 text-sm" />
            <span class="font-bold text-gray-600 dark:text-gray-400">{{ item.nombreTecnico || 'Sin asignar' }}</span>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button @click="abrirModalEdicion(item)" class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm" title="Editar">
              <Icon icon="mdi:pencil" class="text-sm" />
            </button>
            <button v-if="item.estado !== 'COMPLETADO' && item.estado !== 'CANCELADO'" @click="abrirModalPago(item)" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm" title="Finalizar">
              <Icon icon="mdi:check-circle" class="text-sm" />
            </button>
            <button @click="confirmarEliminar(item)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm" title="Eliminar">
              <Icon icon="mdi:trash-can-outline" class="text-sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="serviciosFiltrados.length > itemsPerPage" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, serviciosFiltrados.length) }} DE {{ serviciosFiltrados.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="goToFirstPage" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
          </button>
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in displayedPages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>
          <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
          <button @click="goToLastPage" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL FORMULARIO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showFormModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9998]" @click.self="showFormModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700 animate-zoomIn">
          <!-- Header -->
          <div class="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-indigo-600 to-violet-700 text-white flex justify-between items-center flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl backdrop-blur-md">
                <Icon :icon="editando ? 'mdi:pencil-box' : 'mdi:plus-box'" class="text-xl sm:text-2xl" />
              </div>
              <div>
                <h2 class="text-sm sm:text-lg font-black leading-none">{{ editando ? 'ACTUALIZAR SERVICIO' : 'NUEVO SERVICIO' }}</h2>
                <p class="text-[8px] sm:text-[10px] text-indigo-100 font-bold tracking-widest uppercase mt-0.5">SYSTEM JJ</p>
              </div>
            </div>
            <button @click="showFormModal = false" class="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors">
              <Icon icon="mdi:close" class="text-xl sm:text-2xl" />
            </button>
          </div>

          <div class="p-3 sm:p-6 overflow-y-auto custom-scrollbar bg-gray-50 dark:bg-gray-900/50 max-h-[calc(95vh-140px)] flex-1">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <!-- Columna Izquierda: Cliente y Detalles -->
              <div class="space-y-3 sm:space-y-4">
                <!-- Tarjeta Cliente -->
                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-visible">
                  <div class="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700 rounded-t-2xl">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:account" class="text-indigo-600 dark:text-indigo-400 text-base sm:text-lg" />
                      </div>
                      Información del Cliente
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4">
                    <div v-if="!editando && !form.idCliente" class="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl mb-3 sm:mb-4">
                      <button @click="modoCliente = 'buscar'" :class="['flex-1 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-bold rounded-lg transition-all', modoCliente === 'buscar' ? 'bg-white dark:bg-gray-800 text-indigo-600 shadow-sm' : 'text-gray-400']">BUSCAR</button>
                      <button @click="modoCliente = 'crear'" :class="['flex-1 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-bold rounded-lg transition-all', modoCliente === 'crear' ? 'bg-white dark:bg-gray-800 text-indigo-600 shadow-sm' : 'text-gray-400']">NUEVO</button>
                    </div>

                    <div v-if="modoCliente === 'buscar' && !form.idCliente" class="relative">
                      <div class="relative">
                        <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base sm:text-lg" />
                        <input v-model="busquedaCliente" placeholder="Buscar por nombre o documento..." class="w-full pl-9 sm:pl-10 pr-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 text-xs sm:text-sm transition-all dark:text-white" />
                      </div>
                      <div v-if="busquedaCliente && clientesFiltrados.length" class="absolute z-[100] mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
                        <div v-for="c in clientesFiltrados" :key="c.idCliente" @click="seleccionarCliente(c)" class="p-2 sm:p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                          <p class="font-bold text-xs sm:text-sm text-gray-800 dark:text-white">{{ c.nombre }} {{ c.apellido }}</p>
                          <p class="text-[9px] sm:text-[10px] text-gray-500">{{ c.documento }} • {{ c.telefono }}</p>
                        </div>
                      </div>
                    </div>

                    <div v-if="modoCliente === 'crear' && !form.idCliente" class="space-y-2 sm:space-y-3 animate-fadeIn">
                      <div class="grid grid-cols-2 gap-2 sm:gap-3">
                        <input v-model="nuevoCliente.nombre" placeholder="Nombre *" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                        <input v-model="nuevoCliente.apellido" placeholder="Apellido" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                      </div>
                      <div class="grid grid-cols-2 gap-2 sm:gap-3">
                        <input v-model="nuevoCliente.documento" placeholder="Documento" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                        <input v-model="nuevoCliente.telefono" placeholder="Teléfono *" class="px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                      </div>
                      <textarea v-model="nuevoCliente.direccion" placeholder="Dirección" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" rows="2"></textarea>
                      <button @click="crearYSeleccionarCliente" class="w-full py-2 bg-indigo-600 text-white rounded-xl text-[10px] sm:text-xs font-black hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">
                        <Icon icon="mdi:account-plus" />
                        GUARDAR Y SELECCIONAR
                      </button>
                    </div>

                    <div v-if="form.idCliente" class="p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/10 dark:to-blue-900/10 rounded-2xl border border-indigo-200 dark:border-indigo-800/50 flex items-center justify-between">
                      <div class="flex items-center gap-2 sm:gap-3">
                        <div class="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
                          <Icon icon="mdi:account-check" class="text-indigo-600 dark:text-indigo-400 text-base sm:text-xl" />
                        </div>
                        <div>
                          <p class="font-bold text-xs sm:text-sm text-gray-800 dark:text-white">{{ clienteActual?.nombre }} {{ clienteActual?.apellido }}</p>
                          <p class="text-[9px] sm:text-[10px] text-gray-500 font-mono">{{ clienteActual?.documento }} • {{ clienteActual?.telefono }}</p>
                        </div>
                      </div>
                      <button v-if="!editando" @click="form.idCliente = null" class="p-1 text-gray-400 hover:text-red-500 transition-colors">
                        <Icon icon="mdi:close-circle" class="text-lg sm:text-xl" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Tarjeta Servicio -->
                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:toolbox" class="text-emerald-600 dark:text-emerald-400 text-base sm:text-lg" />
                      </div>
                      Detalles del Servicio
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Descripción del Servicio *</label>
                      <textarea v-model="form.descripcion" rows="2" placeholder="¿Qué se va a realizar?" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white"></textarea>
                    </div>
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Dirección *</label>
                      <textarea v-model="form.direccion" rows="2" placeholder="Lugar donde se realizará el servicio" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white"></textarea>
                    </div>
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Ubicación GPS (Link)</label>
                      <input v-model="form.ubicacionGps" placeholder="https://maps.google.com/..." class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Columna Derecha: Asignación y Fechas -->
              <div class="space-y-3 sm:space-y-4">
                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:calendar-clock" class="text-orange-600 dark:text-orange-400 text-base sm:text-lg" />
                      </div>
                      Planificación y Prioridad
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div class="grid grid-cols-2 gap-2 sm:gap-4">
                      <div class="col-span-2">
                        <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">PRIORIDAD</label>
                        <select v-model="form.prioridad" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-[10px] sm:text-xs font-black dark:bg-gray-700 dark:text-white">
                          <option value="BAJA">🟢 BAJA</option>
                          <option value="MEDIA">🟡 MEDIA</option>
                          <option value="ALTA">🔴 ALTA</option>
                          <option value="URGENTE">🔥 URGENTE</option>
                        </select>
                      </div>
                      <div class="col-span-2 sm:col-span-1">
                        <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Fecha Programada</label>
                        <input v-model="form.fechaProgramada" type="datetime-local" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                      </div>
                      <div class="col-span-2 sm:col-span-1">
                        <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Fecha Finalización</label>
                        <input v-model="form.fechaFinalizacion" type="datetime-local" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- TARJETA DE ASIGNACIÓN Y COSTOS - CON MODAL DE TÉCNICOS -->
                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <h2 class="text-xs sm:text-sm font-bold text-gray-800 dark:text-white flex items-center">
                      <div class="p-1 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-2">
                        <Icon icon="mdi:account-wrench" class="text-purple-600 dark:text-purple-400 text-base sm:text-lg" />
                      </div>
                      Asignación y Costos
                    </h2>
                  </div>
                  <div class="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <!-- TÉCNICO ASIGNADO - CON MODAL -->
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">
                        Técnico Asignado
                      </label>
                      
                      <!-- Botón para abrir modal de selección -->
                      <button 
                        @click="abrirModalTecnicos"
                        class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-500 transition-all group flex items-center justify-between"
                      >
                        <div class="flex items-center gap-3">
                          <div class="p-2 rounded-xl" :class="form.idTecnico ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-200 dark:bg-gray-700'">
                            <Icon 
                              :icon="form.idTecnico ? 'mdi:account-check' : 'mdi:account-plus'" 
                              :class="form.idTecnico ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'"
                              class="text-lg"
                            />
                          </div>
                          <div class="text-left">
                            <p class="font-bold text-sm text-gray-800 dark:text-white">
                              {{ nombreTecnicoAsignado || 'Seleccionar Técnico' }}
                            </p>
                            <p class="text-[9px] text-gray-400 font-medium">
                              {{ form.idTecnico ? 'Técnico asignado ✓' : 'Haz clic para buscar y asignar' }}
                            </p>
                          </div>
                        </div>
                        <Icon 
                          icon="mdi:chevron-right" 
                          class="text-gray-400 group-hover:text-purple-500 transition-colors text-xl" 
                        />
                      </button>
                      
                      <!-- Badge del técnico seleccionado -->
                      <div v-if="form.idTecnico" class="mt-2 flex items-center gap-2 bg-purple-50 dark:bg-purple-900/20 px-3 py-1.5 rounded-lg">
                        <div class="w-6 h-6 bg-purple-200 dark:bg-purple-800 rounded-full flex items-center justify-center text-[8px] font-black text-purple-700 dark:text-purple-300">
                          {{ (nombreTecnicoAsignado || 'T').charAt(0) }}
                        </div>
                        <span class="text-xs font-bold text-purple-700 dark:text-purple-300">{{ nombreTecnicoAsignado }}</span>
                        <button @click="form.idTecnico = null" class="ml-auto text-gray-400 hover:text-red-500 transition-colors">
                          <Icon icon="mdi:close-circle" class="text-sm" />
                        </button>
                      </div>
                    </div>
                    
                    <!-- Costo -->
                    <div>
                      <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Costo ($)</label>
                      <input v-model="form.costo" type="number" step="0.01" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs font-bold dark:bg-gray-700 dark:text-white" />
                      <p class="text-[8px] text-gray-400 mt-1 ml-1">⚠️ El costo se guarda sin IVA. El total final incluye 16% de IVA.</p>
                    </div>
                  </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-3 sm:p-4">
                  <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase mb-1">Observaciones</label>
                  <textarea v-model="form.observaciones" rows="2" class="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs dark:bg-gray-700 dark:text-white resize-none" placeholder="Detalles adicionales..."></textarea>
                </div>
              </div>
            </div>
          </div>

          <div class="px-4 sm:px-6 py-3 sm:py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0">
            <button @click="showFormModal = false" class="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-[10px] sm:text-xs">
              CANCELAR
            </button>
            <button @click="guardarServicio" :disabled="saving || !form.idCliente || !form.descripcion" class="w-full sm:w-auto px-6 sm:px-8 py-2 bg-indigo-600 text-white rounded-xl font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none flex items-center justify-center gap-2 text-[10px] sm:text-xs uppercase tracking-widest disabled:opacity-50">
              <Icon :icon="saving ? 'mdi:loading' : (editando ? 'mdi:content-save' : 'mdi:check-circle')" :class="saving ? 'animate-spin' : ''" />
              {{ saving ? 'Guardando...' : (editando ? 'Actualizar' : 'Confirmar') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE SELECCIÓN DE TÉCNICOS -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showTecnicoModal" class="fixed inset-0 z-[9999] overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showTecnicoModal = false"></div>
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden transform transition-all animate-zoomIn">
          <div class="p-4 sm:p-6 bg-gradient-to-r from-purple-600 to-violet-700">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-white/20 rounded-xl">
                  <Icon icon="mdi:account-wrench" class="text-2xl text-white" />
                </div>
                <div>
                  <h3 class="text-lg font-black text-white uppercase tracking-tighter">Seleccionar Técnico</h3>
                  <p class="text-[10px] font-bold text-purple-100 uppercase tracking-widest">Elige el técnico responsable</p>
                </div>
              </div>
              <button @click="showTecnicoModal = false" class="p-2 hover:bg-white/10 rounded-xl transition-all">
                <Icon icon="mdi:close" class="text-2xl text-white" />
              </button>
            </div>
          </div>
          <div class="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 bg-gray-50 dark:bg-gray-900/50">
            <div class="mb-4">
              <div class="relative group">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-purple-500 transition-colors text-xl" />
                <input
                  v-model="tecnicoSearchModal"
                  type="text"
                  placeholder="Buscar por nombre, apellido o especialidad..."
                  class="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 rounded-xl transition-all outline-none text-sm font-medium dark:text-white"
                  autofocus
                />
              </div>
              <p class="text-[10px] text-gray-400 mt-1.5 font-medium ml-1">
                {{ tecnicosFiltradosModal.length }} técnico{{ tecnicosFiltradosModal.length !== 1 ? 's' : '' }} disponible{{ tecnicosFiltradosModal.length !== 1 ? 's' : '' }}
              </p>
            </div>
            <div v-if="tecnicosFiltradosModal.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div 
                v-for="t in tecnicosFiltradosModal" 
                :key="t.idTecnico"
                @click="seleccionarTecnicoModal(t)"
                class="p-4 bg-white dark:bg-gray-800 rounded-xl border-2 transition-all cursor-pointer group hover:shadow-lg"
                :class="[
                  form.idTecnico === t.idTecnico 
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md shadow-purple-500/10' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700',
                  tecnicoSeleccionadoTemp?.idTecnico === t.idTecnico ? 'ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-gray-800' : ''
                ]"
              >
                <div class="flex items-start gap-3">
                  <div class="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black flex-shrink-0" 
                       :class="form.idTecnico === t.idTecnico ? 'bg-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'">
                    {{ (t.nombre || 'T').charAt(0) }}{{ (t.apellido || '').charAt(0) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-bold text-gray-900 dark:text-white text-sm truncate">{{ t.nombre }} {{ t.apellido }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 truncate"><Icon icon="mdi:email" class="inline text-xs mr-1" /> {{ t.email || 'Sin email' }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 truncate"><Icon icon="mdi:phone" class="inline text-xs mr-1" /> {{ t.telefono || 'Sin teléfono' }}</p>
                    <div class="mt-1.5 flex flex-wrap gap-1">
                      <span class="text-[8px] font-black px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">ID: {{ t.idTecnico }}</span>
                      <span v-if="t.especialidad" class="text-[8px] font-black px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{{ t.especialidad }}</span>
                    </div>
                  </div>
                  <div class="flex-shrink-0 mt-1">
                    <div :class="[
                      'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                      form.idTecnico === t.idTecnico ? 'bg-purple-500 border-purple-500' : 'border-gray-300 dark:border-gray-600 group-hover:border-purple-400'
                    ]">
                      <Icon v-if="form.idTecnico === t.idTecnico" icon="mdi:check" class="text-white text-xs" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <Icon icon="mdi:account-search" class="text-5xl text-gray-400 mx-auto mb-3" />
              <p class="text-gray-600 dark:text-gray-400 font-medium">No se encontraron técnicos</p>
              <p class="text-xs text-gray-400 mt-1">Intenta con otro término de búsqueda</p>
            </div>
          </div>
          <div class="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex justify-between items-center">
            <div>
              <span v-if="form.idTecnico" class="text-xs font-bold text-purple-600 dark:text-purple-400 flex items-center gap-1">
                <Icon icon="mdi:check-circle" class="text-sm" /> Técnico seleccionado
              </span>
              <span v-else class="text-xs text-gray-400">Ningún técnico seleccionado</span>
            </div>
            <div class="flex gap-2">
              <button @click="showTecnicoModal = false" class="px-6 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">Cancelar</button>
              <button @click="confirmarSeleccionTecnico" :disabled="!tecnicoSeleccionadoTemp" class="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-violet-700 text-white rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-purple-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                <Icon icon="mdi:check" /> Confirmar
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE PAGO CON CRÉDITO / FIADO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showPaymentModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="showPaymentModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col animate-zoomIn">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <Icon icon="mdi:cash" class="text-white text-lg sm:text-xl" />
              <div>
                <h3 class="text-sm sm:text-base font-black text-white">Finalizar Servicio</h3>
                <p class="text-[8px] sm:text-[10px] text-emerald-100">#{{ itemPago?.numeroServicio }}</p>
              </div>
            </div>
            <button @click="showPaymentModal = false" class="text-white/80 hover:text-white">
              <Icon icon="mdi:close" class="text-xl sm:text-2xl" />
            </button>
          </div>

          <div class="p-4 sm:p-5 overflow-y-auto flex-1 custom-scrollbar">
            <div class="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg mb-4">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-[9px] text-gray-500 dark:text-gray-400 font-bold">Cliente</p>
                  <p class="text-sm font-black text-gray-800 dark:text-white">{{ itemPago?.nombreCliente }}</p>
                </div>
                <div class="text-right">
                  <p class="text-[9px] text-gray-500 dark:text-gray-400 font-bold">Costo Base</p>
                  <p class="text-sm font-black text-emerald-600">${{ (itemPago?.costo || 0).toFixed(2) }}</p>
                </div>
              </div>
            </div>

            <div class="border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl p-3 sm:p-4 mb-4">
              <div class="flex justify-between items-center mb-2">
                <h4 class="text-[10px] sm:text-xs font-black text-gray-500 dark:text-gray-400 uppercase flex items-center gap-2">
                  <Icon icon="mdi:cart-plus" class="text-emerald-600 text-sm" /> Productos / Servicios Adicionales
                </h4>
                <div class="flex gap-2">
                  <button @click="abrirModalConfiguracionItem('servicio')" class="text-[8px] font-black px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-1">
                    <Icon icon="mdi:wrench" class="text-sm" /> Servicio
                  </button>
                  <button @click="abrirModalConfiguracionItem('producto')" class="text-[8px] font-black px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-1">
                    <Icon icon="mdi:package-variant" class="text-sm" /> Producto
                  </button>
                </div>
              </div>
              <div v-if="itemsPago.length > 0" class="space-y-1.5 mt-2">
                <div v-for="(it, idx) in itemsPago" :key="it.uuid" class="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <div :class="it.tipo === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'" class="p-1 rounded-lg shrink-0">
                      <Icon :icon="it.tipo === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-xs" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-[10px] font-bold text-gray-800 dark:text-white truncate">{{ it.nombre }}</p>
                      <p class="text-[8px] text-gray-400 font-black uppercase tracking-widest">{{ it.tipo }}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-black text-emerald-600 dark:text-emerald-400">${{ (it.precio_total || it.precio || 0).toFixed(2) }}</span>
                    <button @click="eliminarItemPago(idx)" class="text-rose-400 hover:text-rose-600 transition-colors p-1 rounded hover:bg-rose-50">
                      <Icon icon="mdi:trash-can-outline" class="text-sm" />
                    </button>
                  </div>
                </div>
                <div class="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600 text-[10px] font-bold">
                  <span class="text-gray-500">Subtotal adicional:</span>
                  <span class="text-emerald-600">${{ totalItemsPago.toFixed(2) }}</span>
                </div>
              </div>
              <div v-else class="text-center py-2">
                <p class="text-[9px] text-gray-400">No hay items adicionales.</p>
              </div>
            </div>

            <div class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-3 rounded-lg mb-4">
              <div class="flex justify-between items-center">
                <span class="text-[10px] font-black text-gray-600 dark:text-gray-400">Costo Base:</span>
                <span class="text-sm font-black text-gray-800 dark:text-white">${{ (itemPago?.costo || 0).toFixed(2) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-[10px] font-black text-gray-600 dark:text-gray-400">Adicionales:</span>
                <span class="text-sm font-black text-emerald-600">${{ totalItemsPago.toFixed(2) }}</span>
              </div>
              <div class="flex justify-between items-center pt-2 border-t-2 border-emerald-300 dark:border-emerald-700">
                <span class="text-sm font-black text-gray-800 dark:text-white">TOTAL:</span>
                <span class="text-xl font-black text-emerald-700 dark:text-emerald-300">${{ totalAPagarConItems.toFixed(2) }}</span>
              </div>
            </div>

            <div>
              <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 mb-1 uppercase">MÉTODO DE PAGO</label>
              <div class="grid grid-cols-5 gap-1 sm:gap-2">
                <button v-for="met in metodosPagoList" :key="met.val" @click="seleccionarMetodo(met.val)" :class="['py-1.5 sm:py-2 rounded-lg text-[8px] sm:text-xs font-black transition-all', pagoForm.metodo === met.val ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700']">
                  <Icon :icon="met.icon" class="text-base sm:text-lg mx-auto mb-0.5" />
                  <span class="text-[6px] sm:text-[8px]">{{ met.label }}</span>
                </button>
              </div>
            </div>

            <div v-if="pagoForm.metodo === 'CREDITO'" class="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg mt-3 border-2 border-purple-300 dark:border-purple-700">
              <h4 class="text-[10px] font-black text-purple-700 dark:text-purple-300 uppercase mb-3 flex items-center gap-2">
                <Icon icon="mdi:calendar-clock" class="text-sm" /> Configuración del Crédito / Fiado
              </h4>
              <div class="space-y-3">
                <div>
                  <label class="block text-[9px] font-black text-gray-600 dark:text-gray-400 uppercase mb-1">📅 Fecha de Pago Acordada <span class="text-red-500">*</span></label>
                  <input v-model="pagoForm.fechaVencimiento" type="date" :min="new Date().toISOString().split('T')[0]" class="w-full px-3 py-2 border-2 border-purple-300 dark:border-purple-700 rounded-xl text-sm font-bold bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500 outline-none" />
                  <p class="text-[8px] text-gray-400 mt-1 ml-1">⚠️ El cliente deberá pagar en esta fecha acordada</p>
                </div>
                <div>
                  <label class="block text-[9px] font-black text-gray-600 dark:text-gray-400 uppercase mb-1">📝 Observaciones del Crédito</label>
                  <textarea v-model="pagoForm.observacionesCredito" rows="2" placeholder="Ej: Cliente pagará el día acordado en efectivo..." class="w-full px-3 py-2 border-2 border-purple-300 dark:border-purple-700 rounded-xl text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500 outline-none resize-none"></textarea>
                </div>
                <div class="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
                  <div class="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span class="text-gray-600 dark:text-gray-400">💰 Total a financiar:</span>
                      <p class="font-black text-purple-700 dark:text-purple-300 text-sm">${{ totalAPagarConItems.toFixed(2) }}</p>
                    </div>
                    <div>
                      <span class="text-gray-600 dark:text-gray-400">🏷️ Tipo:</span>
                      <p class="font-black text-purple-700 dark:text-purple-300 uppercase text-sm">FIADO / EMPRESA</p>
                    </div>
                    <div class="col-span-2">
                      <span class="text-gray-600 dark:text-gray-400">📅 Fecha acordada:</span>
                      <p class="font-black text-purple-700 dark:text-purple-300">{{ pagoForm.fechaVencimiento ? formatearFecha(pagoForm.fechaVencimiento) : '⚠️ No seleccionada' }}</p>
                    </div>
                  </div>
                </div>
                <div class="bg-amber-50 dark:bg-amber-900/20 p-2.5 rounded-lg border border-amber-200 dark:border-amber-800/40">
                  <div class="flex gap-2 items-start">
                    <Icon icon="mdi:information-outline" class="text-amber-500 text-base shrink-0 mt-0.5" />
                    <p class="text-[9px] text-amber-700 dark:text-amber-300 font-bold leading-tight">
                      🔹 El servicio pasará a estado <span class="uppercase font-black">"EN PROCESO"</span>.<br>
                      🔹 Se registrará un crédito de <span class="font-black">${{ totalAPagarConItems.toFixed(2) }}</span> con fecha de pago acordada.<br>
                      🔹 Podrás cobrar el crédito desde el módulo de créditos.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="pagoForm.metodo !== 'CREDITO'" class="bg-amber-50 dark:bg-amber-900/20 p-2.5 sm:p-3 rounded-lg mt-3">
              <div class="flex items-center justify-between mb-1 sm:mb-2">
                <div class="flex items-center gap-1 sm:gap-2">
                  <Icon icon="mdi:cash-register" class="text-amber-600 text-sm sm:text-base" />
                  <span class="text-[9px] sm:text-[10px] font-black">Registrar Cobro en Caja</span>
                </div>
                <button @click="pagoForm.registrarEnCaja = !pagoForm.registrarEnCaja" :class="['relative w-8 sm:w-10 h-4 sm:h-5 rounded-full transition-colors', pagoForm.registrarEnCaja ? 'bg-emerald-600' : 'bg-gray-400']">
                  <span :class="['absolute top-0.5 w-3 sm:w-4 h-3 sm:h-4 rounded-full bg-white transition-transform', pagoForm.registrarEnCaja ? 'right-0.5' : 'left-0.5']"></span>
                </button>
              </div>
            </div>

            <template v-if="pagoForm.metodo !== 'CREDITO' && pagoForm.registrarEnCaja">
              <div class="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                <p class="text-[9px] sm:text-[10px] font-black text-gray-400 mb-2 text-center">INFORMACIÓN DEL PAGO</p>
                <div class="mb-2 sm:mb-3">
                  <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 mb-1 uppercase">TIPO DE MONEDA</label>
                  <div class="flex gap-2">
                    <button @click="pagoForm.moneda = 'USD'" :class="['flex-1 py-1.5 sm:py-2 rounded-lg text-[10px] sm:text-sm font-black', pagoForm.moneda === 'USD' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700']">USD $</button>
                    <button @click="pagoForm.moneda = 'VES'" :class="['flex-1 py-1.5 sm:py-2 rounded-lg text-[10px] sm:text-sm font-black', pagoForm.moneda === 'VES' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700']">VES Bs.</button>
                  </div>
                </div>
                <div v-if="pagoForm.moneda === 'VES'" class="mb-2 sm:mb-3">
                  <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 mb-1 uppercase">TASA DE CAMBIO (USD → VES)</label>
                  <input v-model="pagoForm.tasaCambio" type="number" step="0.01" placeholder="Ej: 40.00" class="w-full px-3 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm" />
                </div>
                <div v-if="pagoForm.metodo !== 'EFECTIVO'" class="mb-2 sm:mb-3">
                  <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 mb-1 uppercase">REFERENCIA / COMPROBANTE</label>
                  <input v-model="pagoForm.referencia" placeholder="Número de referencia" class="w-full px-3 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm" />
                </div>
                <div>
                  <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 mb-1 uppercase">MONTO A ABONAR</label>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs sm:text-sm">{{ pagoForm.moneda === 'USD' ? '$' : 'Bs.' }}</span>
                    <input v-model="pagoForm.monto" type="number" step="0.01" placeholder="0.00" class="w-full pl-7 sm:pl-8 pr-3 py-1.5 sm:py-2 border rounded-lg text-xs sm:text-sm font-black" />
                  </div>
                </div>
              </div>
            </template>

            <div class="bg-blue-50 dark:bg-blue-900/20 p-2.5 sm:p-3 rounded-lg mt-3">
              <div class="flex gap-1 sm:gap-2">
                <Icon icon="mdi:information" class="text-blue-500 text-base sm:text-lg shrink-0" />
                <p class="text-[8px] sm:text-[9px] text-blue-700 dark:text-blue-300 font-bold leading-tight">
                  <span v-if="pagoForm.metodo === 'CREDITO'">✅ El servicio se marcará como "EN PROCESO" y el crédito/fiado quedará registrado con la fecha acordada.</span>
                  <span v-else-if="!pagoForm.registrarEnCaja">El servicio se marcará como COMPLETADO sin registrar dinero en caja.</span>
                  <span v-else>El servicio se marcará como COMPLETADO y el pago se registrará en caja.</span>
                </p>
              </div>
            </div>
          </div>

          <div class="px-4 sm:px-5 py-3 bg-gray-50 dark:bg-gray-900/50 flex flex-col sm:flex-row gap-2 flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
            <button @click="showPaymentModal = false" class="w-full sm:flex-1 py-2 border rounded-lg font-black text-[10px] sm:text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="finalizarConPago" :disabled="paying || (pagoForm.metodo === 'CREDITO' && !pagoForm.fechaVencimiento)" class="w-full sm:flex-1 py-2 bg-emerald-600 text-white rounded-lg font-black text-[10px] sm:text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              <Icon v-if="paying" icon="mdi:loading" class="animate-spin" />
              <Icon v-else icon="mdi:check-circle" />
              {{ paying ? 'Procesando...' : pagoForm.metodo === 'CREDITO' ? 'Crear Crédito / Fiado' : 'Finalizar Servicio' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CONFIGURACIÓN ÍTEM -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showItemConfigModal" class="fixed inset-0 z-[10000] overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeItemConfigModal"></div>
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden transform transition-all scale-100 animate-fadeIn">
          <div class="p-5 bg-gradient-to-r" :class="itemConfigTipo === 'servicio' ? 'from-green-600 to-emerald-700' : 'from-blue-600 to-indigo-700'">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-white/20 rounded-xl">
                  <Icon :icon="itemConfigTipo === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-2xl text-white" />
                </div>
                <div>
                  <h3 class="text-lg font-black text-white uppercase tracking-tighter">Configurar {{ itemConfigTipo === 'servicio' ? 'Servicio' : 'Producto' }}</h3>
                  <p class="text-[9px] font-bold text-white/70 uppercase tracking-widest">Ajusta precio, garantía y cantidad</p>
                </div>
              </div>
              <button @click="closeItemConfigModal" class="p-2 hover:bg-white/10 rounded-xl transition-all">
                <Icon icon="mdi:close" class="text-2xl text-white" />
              </button>
            </div>
          </div>
          <div class="p-5 space-y-4 bg-gray-50/50 dark:bg-gray-900/50">
            <div class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <p class="text-[9px] font-black text-gray-400 uppercase tracking-wider mb-1">Ítem seleccionado</p>
              <p class="font-bold text-gray-800 dark:text-white text-sm">{{ itemConfigSeleccionado?.nombre || '' }}</p>
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-2">PRECIO UNITARIO (USD)</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 font-black text-green-600 text-sm">$</span>
                <input v-model.number="itemConfigPrecio" type="number" step="0.01" min="0.01" class="w-full pl-8 pr-3 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-lg font-black text-green-600 dark:text-green-400 outline-none focus:border-green-500 transition-all text-right" />
              </div>
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-2">GARANTÍA (DÍAS)</label>
              <div class="flex gap-2">
                <input v-model.number="itemConfigGarantia" type="number" step="1" min="0" class="flex-1 px-3 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-sm font-bold outline-none focus:border-green-500 transition-all" />
                <div class="px-3 py-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-[9px] font-black text-gray-500 uppercase tracking-wider">{{ formatearGarantia(itemConfigGarantia || 0) }}</div>
              </div>
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-2">CANTIDAD</label>
              <div class="flex items-center gap-3">
                <button @click="itemConfigCantidad = Math.max(1, itemConfigCantidad - 1)" class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center font-black text-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
                  <Icon icon="mdi:minus" class="text-lg" />
                </button>
                <input v-model.number="itemConfigCantidad" type="number" step="1" min="1" class="w-20 px-3 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-center text-lg font-black outline-none focus:border-green-500 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                <button @click="itemConfigCantidad = Math.min(999, itemConfigCantidad + 1)" class="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center font-black text-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
                  <Icon icon="mdi:plus" class="text-lg" />
                </button>
                <div class="flex-1 text-right">
                  <p class="text-[8px] text-gray-400 uppercase tracking-wider">TOTAL</p>
                  <p class="text-xl font-black text-green-600 dark:text-green-400">${{ (itemConfigPrecio * itemConfigCantidad).toFixed(2) }}</p>
                </div>
              </div>
            </div>
            <div class="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-xl border border-amber-100 dark:border-amber-800/40 flex gap-2">
              <Icon icon="mdi:information-outline" class="text-amber-500 text-sm shrink-0" />
              <p class="text-[9px] font-bold text-amber-700 dark:text-amber-300 leading-snug">La garantía se aplica sobre el ítem completo. El precio final se multiplica por la cantidad seleccionada.</p>
            </div>
          </div>
          <div class="p-5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
            <button @click="agregarItemPagoConConfiguracion" class="w-full py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 font-black text-sm uppercase tracking-wider shadow-lg active:scale-95" :class="itemConfigTipo === 'servicio' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'">
              <Icon icon="mdi:check-circle" class="text-lg" /> AGREGAR A LA FACTURA
            </button>
            <button @click="closeItemConfigModal" class="w-full mt-2 py-2 text-[10px] font-black text-gray-400 hover:text-gray-600 uppercase tracking-widest transition-all">Cancelar</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CATÁLOGO -->
    <Teleport to="body">
      <div v-if="showCatalogModal" class="fixed inset-0 z-[10001] overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showCatalogModal = false"></div>
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden transform transition-all scale-100 animate-fadeIn">
          <div class="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div class="flex justify-between items-center">
              <h3 class="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <div class="p-2 rounded-lg" :class="catalogType === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'">
                  <Icon :icon="catalogType === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-xl" />
                </div>
                Seleccionar {{ catalogType === 'servicio' ? 'Servicio' : 'Producto' }}
              </h3>
              <button @click="showCatalogModal = false" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl">
                <Icon icon="mdi:close" class="text-2xl text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div class="mt-4">
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="catalogSearch" type="text" placeholder="Buscar por nombre..." class="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 font-medium outline-none" autocomplete="off" />
              </div>
            </div>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto flex-1 custom-scrollbar">
            <div v-if="filteredCatalogItems.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div v-for="item in filteredCatalogItems" :key="item.id" @click="seleccionarItemCatalogo(item)" class="p-4 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] bg-white dark:bg-gray-800" :class="catalogType === 'servicio' ? 'hover:border-green-500' : 'hover:border-blue-500'">
                <div class="flex gap-3">
                  <div class="p-3 rounded-lg h-fit" :class="catalogType === 'servicio' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'">
                    <Icon :icon="catalogType === 'servicio' ? 'mdi:wrench' : 'mdi:package'" class="text-2xl" :class="catalogType === 'servicio' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'" />
                  </div>
                  <div class="flex-1">
                    <h4 class="font-semibold text-gray-900 dark:text-white">{{ item.nombre }}</h4>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ catalogType === 'servicio' ? 'Servicio Técnico' : 'Producto' }}</p>
                    <div class="mt-3 flex justify-between items-center">
                      <span class="text-sm text-gray-600 dark:text-gray-400">Precio USD:</span>
                      <span class="text-lg font-bold" :class="catalogType === 'servicio' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'">${{ (catalogType === 'servicio' ? (item.precioUsd || 0) : (item.precio_venta_usd || 0)).toFixed(2) }}</span>
                    </div>
                    <div v-if="(catalogType === 'servicio' && item.garantiaDias) || (catalogType === 'producto' && item.garantia_meses)" class="mt-2">
                      <span class="text-[9px] font-black bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                        🛡️ Garantía: {{ catalogType === 'servicio' ? formatearGarantia(item.garantiaDias) : formatearGarantia((item.garantia_meses || 0) * 30) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <Icon icon="mdi:magnify-close" class="text-5xl text-gray-400 mx-auto mb-3" />
              <p class="text-gray-600 dark:text-gray-400">No se encontraron resultados</p>
            </div>
          </div>
          <div class="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div class="flex justify-between items-center">
              <p class="text-sm text-gray-600 dark:text-gray-400">Seleccione un elemento para configurarlo</p>
              <button @click="showCatalogModal = false" class="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 font-medium">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CONFIRMACIÓN ELIMINAR -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[10002]" @click.self="showConfirmModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoomIn">
          <div class="p-5 sm:p-6 text-center">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon icon="mdi:alert-octagon" class="text-rose-500 text-3xl sm:text-4xl" />
            </div>
            <h3 class="text-base sm:text-lg font-black text-gray-900 dark:text-white mb-2">¿Eliminar Servicio?</h3>
            <p class="text-xs sm:text-sm text-gray-500 mb-3">Esta acción no se puede deshacer.</p>
            <p class="font-bold text-rose-600 mb-4 text-sm sm:text-base">#{{ itemEliminar?.numeroServicio }}</p>
            <div class="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button @click="showConfirmModal = false" class="w-full sm:flex-1 py-2 border border-gray-200 dark:border-gray-700 rounded-xl font-black text-[10px] sm:text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
              <button @click="ejecutarEliminacion" class="w-full sm:flex-1 py-2 bg-rose-600 text-white rounded-xl font-black text-[10px] sm:text-xs hover:bg-rose-700 transition-all shadow-lg shadow-rose-200 dark:shadow-none">Eliminar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import api from '@/api/serviciosExternos.js';
import { obtenerClientesActivos, crearCliente } from '@/api/clientes.js';
import { obtenerTecnicos } from '@/api/tecnicos.js';
import { getCurrentUser } from '@/api/index.js';
import { obtenerServicios } from '@/api/servicios.js';
import { obtenerProductos } from '@/api/productos.js';
import { actualizarStockProducto } from '@/api/productos.js';
import { crearVenta } from '@/api/ventas.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';

export default {
  name: 'ServicioExternoList',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      servicios: [],
      clientes: [],
      tecnicos: [],
      catServicios: [],
      catProductos: [],
      loading: false,
      saving: false,
      paying: false,
      filtroBusqueda: '',
      filtroEstado: '',
      currentPage: 1,
      itemsPerPage: 5,
      maxVisiblePages: 10,
      showFormModal: false,
      editando: false,
      modoCliente: 'buscar',
      busquedaCliente: '',
      nuevoCliente: { nombre: '', apellido: '', documento: '', telefono: '', direccion: '' },
      form: {
        idServicioExterno: null,
        idCliente: null,
        idTecnico: null,
        descripcion: '',
        direccion: '',
        ubicacionGps: '',
        fechaProgramada: null,
        fechaFinalizacion: null,
        fechaPago: null,
        costo: 0,
        prioridad: 'MEDIA',
        estado: 'PENDIENTE',
        observaciones: ''
      },
      showTecnicoModal: false,
      tecnicoSearchModal: '',
      tecnicoSeleccionadoTemp: null,
      showPaymentModal: false,
      itemPago: null,
      itemsPago: [],
      pagoForm: {
        metodo: 'EFECTIVO',
        referencia: '',
        monto: 0,
        moneda: 'USD',
        tasaCambio: null,
        registrarEnCaja: true,
        fechaVencimiento: null,
        observacionesCredito: ''
      },
      metodosPagoList: [
        { val: 'EFECTIVO', label: 'Efectivo', icon: 'mdi:cash' },
        { val: 'TRANSFERENCIA', label: 'Transferencia', icon: 'mdi:bank-transfer' },
        { val: 'PAGO_MOVIL', label: 'Pago Móvil', icon: 'mdi:cellphone' },
        { val: 'ZELLE', label: 'Zelle', icon: 'mdi:send' },
        { val: 'CREDITO', label: 'Crédito / Fiado', icon: 'mdi:domain' }
      ],
      showItemConfigModal: false,
      itemConfigTipo: 'servicio',
      itemConfigSeleccionado: null,
      itemConfigPrecio: 0,
      itemConfigGarantia: 0,
      itemConfigCantidad: 1,
      showCatalogModal: false,
      catalogType: 'servicio',
      catalogSearch: '',
      showConfirmModal: false,
      itemEliminar: null,
      tecnicoSearch: ''
    };
  },
  computed: {
    nombreTecnicoAsignado() {
      if (!this.form.idTecnico) return '';
      const t = this.tecnicos.find(x => x.idTecnico === this.form.idTecnico);
      return t ? `${t.nombre} ${t.apellido}` : '';
    },
    serviciosFiltrados() {
      let result = this.servicios;
      if (this.filtroEstado) result = result.filter(s => s.estado === this.filtroEstado);
      if (this.filtroBusqueda) {
        const q = this.filtroBusqueda.toLowerCase();
        result = result.filter(s => 
          (s.nombreCliente?.toLowerCase() || '').includes(q) ||
          (s.numeroServicio?.toString() || '').includes(q) ||
          (s.descripcion?.toLowerCase() || '').includes(q) ||
          (s.nombreTecnico?.toLowerCase() || '').includes(q)
        );
      }
      return result;
    },
    clientesFiltrados() {
      if (!this.busquedaCliente) return [];
      const q = this.busquedaCliente.toLowerCase();
      return this.clientes.filter(c => 
        (c.nombre?.toLowerCase() || '').includes(q) || 
        (c.apellido?.toLowerCase() || '').includes(q) ||
        (c.documento?.toLowerCase() || '').includes(q)
      ).slice(0, 5);
    },
    clienteActual() {
      return this.clientes.find(c => c.idCliente === this.form.idCliente);
    },
    totalPages() {
      return Math.ceil(this.serviciosFiltrados.length / this.itemsPerPage);
    },
    tecnicosFiltrados() {
      if (!this.tecnicoSearch) return this.tecnicos;
      const q = this.tecnicoSearch.toLowerCase();
      return this.tecnicos.filter(t => 
        (t.nombre?.toLowerCase() || '').includes(q) || 
        (t.apellido?.toLowerCase() || '').includes(q)
      );
    },
    tecnicosFiltradosModal() {
      if (!this.tecnicoSearchModal) return this.tecnicos;
      const q = this.tecnicoSearchModal.toLowerCase().trim();
      return this.tecnicos.filter(t => 
        (t.nombre?.toLowerCase() || '').includes(q) || 
        (t.apellido?.toLowerCase() || '').includes(q) ||
        (t.email?.toLowerCase() || '').includes(q) ||
        (t.especialidad?.toLowerCase() || '').includes(q) ||
        (t.telefono || '').includes(q)
      );
    },
    paginatedServicios() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.serviciosFiltrados.slice(start, start + this.itemsPerPage);
    },
    displayedPages() {
      const total = this.totalPages;
      const max = this.maxVisiblePages;
      const current = this.currentPage;
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
      let start = Math.max(1, current - Math.floor(max / 2));
      let end = start + max - 1;
      if (end > total) { end = total; start = Math.max(1, end - max + 1); }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    totalItemsPago() {
      return this.itemsPago.reduce((sum, it) => sum + (parseFloat(it.precio_total || it.precio || 0) || 0), 0);
    },
    totalAPagarConItems() {
      return (this.itemPago?.costo || 0) + this.totalItemsPago;
    },
    filteredCatalogItems() {
      const termino = this.catalogSearch.toLowerCase().trim();
      let items = [];
      if (this.catalogType === 'servicio') {
        items = this.catServicios.map(s => ({ ...s, id: s.idServicio, precio: s.precioUsd, garantiaDias: s.garantiaDias || 0 }));
      } else {
        items = this.catProductos.map(p => ({ ...p, id: p.idProducto, precio: p.precio_venta_usd, garantiaMeses: p.garantia_meses || 0 }));
      }
      if (!termino) return items.slice(0, 50);
      return items.filter(x => (x.nombre || '').toLowerCase().includes(termino)).slice(0, 50);
    }
  },
  async mounted() {
    await this.cargarCatalogos();
    await this.cargarServicios();
  },
  methods: {
    goToFirstPage() { this.currentPage = 1; },
    goToLastPage() { this.currentPage = this.totalPages; },
    obtenerFechaHoraLocal() {
      const ahora = new Date();
      const año = ahora.getFullYear();
      const mes = String(ahora.getMonth() + 1).padStart(2, '0');
      const dia = String(ahora.getDate()).padStart(2, '0');
      const horas = String(ahora.getHours()).padStart(2, '0');
      const minutos = String(ahora.getMinutes()).padStart(2, '0');
      return `${año}-${mes}-${dia}T${horas}:${minutos}`;
    },
    formatearGarantia(dias) {
      if (!dias || dias === 0) return 'Sin garantía';
      if (dias >= 365) return `${Math.floor(dias / 365)} año${Math.floor(dias / 365) !== 1 ? 's' : ''}`;
      if (dias >= 30) return `${Math.floor(dias / 30)} mes${Math.floor(dias / 30) !== 1 ? 'es' : ''}`;
      return `${dias} día${dias !== 1 ? 's' : ''}`;
    },
    formatearFecha(fecha) {
      if (!fecha) return 'N/A';
      try {
        const d = new Date(fecha);
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch { return fecha; }
    },
    async cargarServicios() {
      this.loading = true;
      try {
        const res = await api.obtenerServicios();
        if (res.success) this.servicios = res.data;
        else this.toast.error(res.message || "Error al cargar servicios");
      } catch (error) {
        this.toast.error("Error al cargar servicios");
      } finally { this.loading = false; }
    },
    async cargarCatalogos() {
      try {
        const [cRes, tRes, sRes, pRes] = await Promise.all([
          obtenerClientesActivos(), obtenerTecnicos(),
          obtenerServicios('activos'), obtenerProductos('activos')
        ]);
        if (cRes.success) this.clientes = cRes.data;
        if (tRes.success) this.tecnicos = tRes.data;
        if (sRes.success) this.catServicios = sRes.data;
        if (pRes.success) this.catProductos = pRes.data;
      } catch (error) { console.error("Error catalogos", error); }
    },
    abrirModalCreacion() {
      this.editando = false;
      this.modoCliente = 'buscar';
      this.busquedaCliente = '';
      this.tecnicoSearch = '';
      const currentUser = getCurrentUser();
      let defaultIdTecnico = null;
      if (currentUser) {
        const userId = currentUser.idUsuario || currentUser.id;
        const tecnicoAsociado = this.tecnicos.find(t => parseInt(t.idUsuario) === parseInt(userId));
        if (tecnicoAsociado) defaultIdTecnico = tecnicoAsociado.idTecnico;
      }
      const fechaActualLocal = this.obtenerFechaHoraLocal();
      this.form = {
        idServicioExterno: null,
        idCliente: null,
        idTecnico: defaultIdTecnico,
        descripcion: '',
        direccion: '',
        ubicacionGps: '',
        fechaProgramada: fechaActualLocal,
        fechaFinalizacion: null,
        fechaPago: null,
        costo: 0,
        prioridad: 'MEDIA',
        estado: 'PENDIENTE',
        observaciones: ''
      };
      this.showFormModal = true;
    },
    abrirModalEdicion(item) {
      this.editando = true;
      const formatearFecha = (fecha) => {
        if (!fecha) return null;
        try { return new Date(fecha).toISOString().slice(0, 16); } catch { return null; }
      };
      this.form = { 
        ...item,
        idTecnico: item.idTecnico ? Number(item.idTecnico) : null,
        fechaProgramada: formatearFecha(item.fechaProgramada),
        fechaFinalizacion: formatearFecha(item.fechaFinalizacion),
        fechaPago: formatearFecha(item.fechaPago)
      };
      this.showFormModal = true;
    },
    seleccionarCliente(c) {
      this.form.idCliente = c.idCliente;
      this.busquedaCliente = '';
      if (!this.form.direccion) this.form.direccion = c.direccion || '';
    },
    async crearYSeleccionarCliente() {
      try {
        const res = await crearCliente(this.nuevoCliente);
        if (res.success) {
          this.toast.success("Cliente creado");
          await this.cargarCatalogos();
          this.form.idCliente = res.data.idCliente;
          this.modoCliente = 'buscar';
          this.nuevoCliente = { nombre: '', apellido: '', documento: '', telefono: '', direccion: '' };
        }
      } catch (error) { this.toast.error("Error al crear cliente"); }
    },
    async guardarServicio() {
      if (!this.form.idCliente) { this.toast.error("Seleccione un cliente"); return; }
      if (!this.form.descripcion?.trim()) { this.toast.error("La descripción es requerida"); return; }
      this.saving = true;
      try {
        const costoBase = parseFloat(this.form.costo) || 0;
        const datosEnviar = {
          idCliente: this.form.idCliente,
          idTecnico: this.form.idTecnico,
          descripcion: this.form.descripcion,
          direccion: this.form.direccion,
          ubicacionGps: this.form.ubicacionGps || null,
          costo: costoBase,
          prioridad: this.form.prioridad,
          observaciones: this.form.observaciones || null,
          fechaProgramada: this.form.fechaProgramada ? new Date(this.form.fechaProgramada).toISOString() : null,
          fechaFinalizacion: this.form.fechaFinalizacion ? new Date(this.form.fechaFinalizacion).toISOString() : null,
          estado: this.form.estado
        };
        console.log('📝 Enviando al backend:', datosEnviar);
        let res;
        if (this.editando) {
          res = await api.actualizarServicio(this.form.idServicioExterno, datosEnviar);
        } else {
          res = await api.crearServicio(datosEnviar);
        }
        if (res.success) {
          this.toast.success(this.editando ? "Actualizado" : "Creado");
          this.showFormModal = false;
          await this.cargarServicios();
        } else {
          this.toast.error(res.message || "Error al guardar");
        }
      } catch (error) {
        console.error("Error:", error);
        this.toast.error("Error al guardar");
      } finally { this.saving = false; }
    },
    abrirModalTecnicos() {
      this.tecnicoSearchModal = '';
      this.tecnicoSeleccionadoTemp = this.form.idTecnico ? this.tecnicos.find(t => t.idTecnico === this.form.idTecnico) : null;
      this.showTecnicoModal = true;
    },
    seleccionarTecnicoModal(tecnico) { this.tecnicoSeleccionadoTemp = tecnico; },
    confirmarSeleccionTecnico() {
      if (this.tecnicoSeleccionadoTemp) {
        this.form.idTecnico = this.tecnicoSeleccionadoTemp.idTecnico;
        this.showTecnicoModal = false;
        this.toast.success(`✅ Técnico ${this.tecnicoSeleccionadoTemp.nombre} ${this.tecnicoSeleccionadoTemp.apellido} asignado`);
      }
    },
    abrirModalPago(item) {
      this.itemPago = item;
      this.itemsPago = [];
      const fecha = new Date();
      fecha.setDate(fecha.getDate() + 30);
      const fechaStr = fecha.toISOString().split('T')[0];
      this.pagoForm = {
        metodo: 'EFECTIVO',
        referencia: '',
        monto: this.totalAPagarConItems || this.itemPago?.costo || 0,
        moneda: 'USD',
        tasaCambio: null,
        registrarEnCaja: true,
        fechaVencimiento: fechaStr,
        observacionesCredito: `Crédito/Fiado generado por servicio externo #${this.itemPago?.numeroServicio || ''}`
      };
      this.showPaymentModal = true;
    },
    seleccionarMetodo(val) {
      this.pagoForm.metodo = val;
      if (val === 'CREDITO') {
        this.pagoForm.registrarEnCaja = false;
        this.pagoForm.monto = this.totalAPagarConItems;
      } else {
        this.pagoForm.registrarEnCaja = true;
        this.pagoForm.monto = this.totalAPagarConItems;
      }
    },
    async crearCreditoParaServicio() {
      if (!this.itemPago || !this.itemPago.idServicioExterno) {
        this.toast.error("No se puede crear el crédito: servicio no válido");
        return;
      }
      if (!this.pagoForm.fechaVencimiento) {
        this.toast.error("Por favor, selecciona la fecha de pago acordada");
        return;
      }
      try {
        const total = this.totalAPagarConItems || this.itemPago.costo || 0;
        if (total <= 0) {
          this.toast.warning("El monto total es 0. No se puede crear un crédito.");
          return;
        }
        const creditoData = {
          idServicioExterno: this.itemPago.idServicioExterno,
          codigoTipoCredito: 'EMP-001',
          numeroCuotas: 1,
          fechaVencimientoBase: new Date(this.pagoForm.fechaVencimiento).toISOString(),
          observaciones: this.pagoForm.observacionesCredito || 
            `Crédito/Fiado por servicio externo #${this.itemPago.numeroServicio} - Monto: $${total.toFixed(2)} - Fecha acordada: ${this.pagoForm.fechaVencimiento}`
        };
        console.log('📝 Enviando al backend (CreditoServicioExterno):', creditoData);
        const response = await api.crearCreditoServicioExterno(creditoData);
        if (response.success) {
          const idCredito = response.data?.idCreditoServicioExterno || response.data?.id;
          this.toast.success(`✅ Crédito/Fiado de $${total.toFixed(2)} creado exitosamente`);
          this.toast.info(`📅 Fecha de pago acordada: ${this.formatearFecha(this.pagoForm.fechaVencimiento)}`);
          await this.actualizarServicioConCredito(this.itemPago.idServicioExterno, idCredito, total);
          this.showPaymentModal = false;
          await this.cargarServicios();
          this.toast.info(`📋 El servicio #${this.itemPago.numeroServicio} ahora está en estado "COMPLETADO" con crédito registrado`);
        } else {
          this.toast.error(response.message || response.error || "Error al crear el crédito");
        }
      } catch (error) {
        console.error("❌ Error creando crédito:", error);
        this.toast.error("Error al crear el crédito: " + error.message);
      }
    },
    async actualizarServicioConCredito(idServicio, idCredito, total) {
      try {
        await api.actualizarEstado(idServicio, 'COMPLETADO');
        const payload = {
          ...this.itemPago,
          estado: 'COMPLETADO',
          metodoPago: 'CREDITO',
          idCreditoServicioExterno: idCredito,
          estadoCredito: 'pendiente',
          observaciones: (this.itemPago.observaciones || '') + 
            `\n📌 Crédito/Fiado: #${idCredito} - Monto: $${total.toFixed(2)} - Fecha acordada: ${this.pagoForm.fechaVencimiento || 'No especificada'}`
        };
        if (this.pagoForm.fechaVencimiento) {
          payload.fechaPagoAcordada = this.pagoForm.fechaVencimiento;
        }
        console.log('📝 Actualizando servicio con crédito:', payload);
        const res = await api.actualizarServicio(idServicio, payload);
        if (res.success) return true;
        else {
          console.error('❌ Error actualizando servicio:', res.message);
          this.toast.warning('El crédito se creó pero no se pudo actualizar el servicio: ' + res.message);
          return false;
        }
      } catch (error) {
        console.error('❌ Error en actualizarServicioConCredito:', error);
        return false;
      }
    },
    abrirModalConfiguracionItem(tipo) {
      this.itemConfigTipo = tipo;
      this.showCatalogModal = true;
      this.catalogType = tipo;
      this.catalogSearch = '';
    },
    seleccionarItemCatalogo(item) {
      this.itemConfigSeleccionado = item;
      if (this.itemConfigTipo === 'servicio') {
        this.itemConfigPrecio = item.precioUsd || 0;
        this.itemConfigGarantia = item.garantiaDias || 0;
      } else {
        this.itemConfigPrecio = item.precio_venta_usd || 0;
        this.itemConfigGarantia = (item.garantia_meses || 0) * 30;
      }
      this.itemConfigCantidad = 1;
      this.showCatalogModal = false;
      this.showItemConfigModal = true;
    },
    agregarItemPagoConConfiguracion() {
      if (!this.itemConfigSeleccionado) return;
      if (this.itemConfigPrecio <= 0) { this.toast.warning("El precio debe ser mayor a cero"); return; }
      if (this.itemConfigCantidad < 1) { this.toast.warning("La cantidad debe ser al menos 1"); return; }
      const precioTotal = this.itemConfigPrecio * this.itemConfigCantidad;
      const nuevoItem = {
        uuid: Math.random().toString(36).substring(7),
        id: this.itemConfigSeleccionado.id,
        tipo: this.itemConfigTipo,
        nombre: this.itemConfigSeleccionado.nombre,
        precio_unitario: this.itemConfigPrecio,
        precio_total: precioTotal,
        precio: precioTotal,
        cantidad: this.itemConfigCantidad,
        garantia: this.itemConfigGarantia,
        stock: this.itemConfigTipo === 'producto' ? (this.itemConfigSeleccionado.stock || 0) : 0,
        costo: this.itemConfigTipo === 'producto' ? (this.itemConfigSeleccionado.precio_compra_usd || 0) : 0
      };
      this.itemsPago.push(nuevoItem);
      if (this.itemConfigGarantia > 0) this.toast.success(`✅ Garantía de ${this.formatearGarantia(this.itemConfigGarantia)} aplicada`);
      this.pagoForm.monto = this.totalAPagarConItems;
      this.closeItemConfigModal();
    },
    closeItemConfigModal() {
      this.showItemConfigModal = false;
      this.itemConfigSeleccionado = null;
      this.itemConfigPrecio = 0;
      this.itemConfigGarantia = 0;
      this.itemConfigCantidad = 1;
    },
    eliminarItemPago(index) {
      this.itemsPago.splice(index, 1);
      this.pagoForm.monto = this.totalAPagarConItems;
    },
    async descontarProductosInventario() {
      const productos = this.itemsPago.filter(item => item.tipo === 'producto' && item.id);
      let errores = [];
      for (const item of productos) {
        try {
          const productRes = await this.obtenerProductoPorId(item.id);
          if (!productRes.success) { errores.push(`No se pudo obtener el producto "${item.nombre}"`); continue; }
          const producto = productRes.data;
          const stockActual = producto.stock || 0;
          const cantidad = item.cantidad || 1;
          if (stockActual < cantidad) { errores.push(`Stock insuficiente para "${item.nombre}". Disponible: ${stockActual}, Requerido: ${cantidad}`); continue; }
          const nuevoStock = stockActual - cantidad;
          const updateRes = await actualizarStockProducto(item.id, nuevoStock);
          if (!updateRes.success) errores.push(`Error al descontar "${item.nombre}"`);
        } catch (error) { errores.push(`Error al procesar "${item.nombre}": ${error.message}`); }
      }
      return { success: errores.length === 0, errores };
    },
    async obtenerProductoPorId(id) {
      try { const { apiFetch } = await import('@/api/index.js'); const response = await apiFetch(`/Producto/${id}`); return { success: true, data: response }; } 
      catch (error) { return { success: false, error: error.message }; }
    },
    async obtenerTasaActual() {
      try { const result = await obtenerTasaVentaActual(); if (result.success && result.data) return result.data.precio_usd_bs || 52.00; return 52.00; } 
      catch { return 52.00; }
    },
    async finalizarConPago() {
      if (this.pagoForm.metodo === 'CREDITO') { await this.crearCreditoParaServicio(); return; }
      this.paying = true;
      try {
        let montoUSD = 0, montoVES = 0;
        if (this.pagoForm.registrarEnCaja && this.pagoForm.monto > 0) {
          if (this.pagoForm.moneda === 'VES' && this.pagoForm.tasaCambio) {
            montoUSD = this.pagoForm.monto / this.pagoForm.tasaCambio;
            montoVES = this.pagoForm.monto;
          } else if (this.pagoForm.moneda === 'USD') {
            montoUSD = this.pagoForm.monto;
          }
        }
        let ventaCreada = null;
        if (this.itemsPago.length > 0) {
          const tasaVenta = this.pagoForm.tasaCambio || await this.obtenerTasaActual();
          const subtotal = this.totalItemsPago;
          const iva = subtotal * 0.16;
          const total = subtotal + iva;
          const detallesVenta = this.itemsPago.map(item => ({
            idProducto: item.tipo === 'producto' ? item.id : null,
            idServicio: item.tipo === 'servicio' ? item.id : null,
            cantidad: item.cantidad || 1,
            precioUnitarioUsd: item.precio_unitario || item.precio || 0,
            subtotalUsd: item.precio_total || item.precio || 0,
            garantiaMeses: Math.floor((item.garantia || 0) / 30),
            garantiaDias: (item.garantia || 0) % 30,
            costoUnitarioUsd: item.costo || 0,
            costoTotalUsd: (item.costo || 0) * (item.cantidad || 1)
          }));
          const ventaPayload = {
            idCliente: this.itemPago.idCliente,
            tipoVenta: 'contado',
            subtotalUsd: subtotal,
            ivaUsd: iva,
            descuentoUsd: 0,
            totalUsd: total,
            costoTotalUsd: this.itemsPago.reduce((sum, it) => sum + ((it.costo || 0) * (it.cantidad || 1)), 0),
            tasaBsUsd: tasaVenta || 52.00,
            idFuenteTasaVenta: 1,
            idVendedor: this.itemPago.idTecnico || null,
            observaciones: `Venta generada por servicio externo #${this.itemPago.numeroServicio}`,
            detalles: detallesVenta,
            estado: 'completada'
          };
          const ventaResponse = await crearVenta(ventaPayload);
          if (ventaResponse.success) {
            ventaCreada = ventaResponse.data;
            this.toast.success(`✅ Venta #${ventaCreada?.numeroFactura || ''} creada automáticamente`);
          } else {
            this.toast.warning('⚠️ El servicio se completó pero hubo un error al crear la venta');
          }
        }
        if (this.itemsPago.length > 0) {
          const result = await this.descontarProductosInventario();
          if (!result.success && result.errores.length > 0) {
            this.toast.warning(`⚠️ Algunos productos no se pudieron descontar: ${result.errores.join(', ')}`);
          }
        }
        const payload = {
          ...this.itemPago,
          estado: 'COMPLETADO',
          metodoPago: this.pagoForm.metodo,
          referenciaPago: this.pagoForm.referencia || null,
          montoPagado: montoUSD || this.totalAPagarConItems,
          fechaPago: new Date().toISOString(),
          idVentaGenerada: ventaCreada?.idVenta || null,
          itemsAdicionales: this.itemsPago.map(item => ({
            tipo: item.tipo,
            id: item.id,
            nombre: item.nombre,
            cantidad: item.cantidad || 1,
            precio: item.precio_total || item.precio || 0
          }))
        };
        const res = await api.actualizarServicio(this.itemPago.idServicioExterno, payload);
        if (res.success) {
          if (this.pagoForm.registrarEnCaja) {
            const pagoCaja = {
              monto: montoUSD || this.totalAPagarConItems,
              metodo: this.pagoForm.metodo,
              referencia: this.pagoForm.referencia,
              monedaOriginal: this.pagoForm.moneda,
              montoOriginal: montoVES > 0 ? montoVES : null,
              tasaCambio: this.pagoForm.tasaCambio,
              itemsAdicionales: this.itemsPago.map(item => ({
                nombre: item.nombre,
                precio: item.precio_total || item.precio || 0,
                cantidad: item.cantidad || 1,
                tipo: item.tipo
              }))
            };
            await api.registrarPagoCaja(this.itemPago, pagoCaja);
            this.toast.success(`Servicio finalizado y $${(montoUSD || this.totalAPagarConItems).toFixed(2)} registrados en caja`);
          } else {
            this.toast.success("Servicio finalizado exitosamente");
          }
          if (this.itemsPago.length > 0) {
            const totalItems = this.itemsPago.reduce((sum, it) => sum + (it.cantidad || 1), 0);
            this.toast.success(`📦 ${this.itemsPago.length} item${this.itemsPago.length > 1 ? 's' : ''} agregado${this.itemsPago.length > 1 ? 's' : ''} (${totalItems} unidad${totalItems > 1 ? 'es' : ''})`);
          }
          if (ventaCreada) {
            this.toast.success(`💳 Venta #${ventaCreada.numeroFactura || ''} creada con ${this.itemsPago.length} item${this.itemsPago.length > 1 ? 's' : ''}`);
          }
          this.showPaymentModal = false;
          await this.cargarServicios();
        } else {
          this.toast.error(res.message || "Error al procesar");
        }
      } catch (error) {
        console.error("Error:", error);
        this.toast.error("Error al procesar: " + error.message);
      } finally { this.paying = false; }
    },
    confirmarEliminar(item) { this.itemEliminar = item; this.showConfirmModal = true; },
    async ejecutarEliminacion() {
      if (!this.itemEliminar) return;
      try {
        const res = await api.eliminarServicio(this.itemEliminar.idServicioExterno);
        if (res.success) { this.toast.success("Eliminado"); this.showConfirmModal = false; await this.cargarServicios(); }
      } catch (error) { this.toast.error("Error al eliminar"); }
    },
    formatearHora(f) { 
      if (!f) return '';
      try { return new Date(f).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }); } 
      catch { return ''; }
    },
    getEstadoStyle(est) {
      const estilos = {
        'PENDIENTE': 'bg-amber-500 text-white border-amber-600 shadow-sm',
        'EN CAMINO': 'bg-blue-500 text-white border-blue-600 shadow-sm',
        'EN PROCESO': 'bg-indigo-500 text-white border-indigo-600 shadow-sm',
        'COMPLETADO': 'bg-emerald-500 text-white border-emerald-600 shadow-sm',
        'CANCELADO': 'bg-rose-500 text-white border-rose-600 shadow-sm'
      };
      return estilos[est] || estilos['PENDIENTE'];
    },
    getPrioridadStyle(p) {
      const estilos = {
        'BAJA': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        'MEDIA': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        'ALTA': 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
        'URGENTE': 'bg-red-600 text-white dark:bg-red-700'
      };
      return estilos[p] || estilos['MEDIA'];
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
.group:hover .group-hover\:border-purple-400 { border-color: #a78bfa; }
</style>